import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, interval, map, switchMap, tap } from 'rxjs';

import { PrivaNotificationsService, ToasterAction } from '@priva/notifications';

import { absoluteRoutePaths } from 'app/core/routing';
import { selectIsUpdateAvailable } from 'app/state/service-worker/service-worker.selectors';

import { checkForUpdates, initializeServiceWorker, registerUpdateAvailable } from './service-worker.actions';

// noinspection JSUnusedGlobalSymbols
@Injectable()
export class ServiceWorkerEffects {
    registerUpdateAvailable$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(initializeServiceWorker),
            filter(() => this.swUpdate.isEnabled),
            switchMap(() => this.swUpdate.versionUpdates),
            filter((e) => e.type === 'VERSION_READY'),
            map(() => registerUpdateAvailable()),
        );
    });

    showToasterNotification$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(initializeServiceWorker),
                switchMap(() => this.store.select(selectIsUpdateAvailable)),
                filter((isUpdateAvailable) => isUpdateAvailable),
                map(() => {
                    const updateActions: ToasterAction[] = [
                        {
                            id: 'updateAction',
                            text: 'Update',
                            title: 'Install update',
                            importance: 'Tertiary',
                            execute: async (toasterId) => {
                                this.notificationService.removeNotification(toasterId);
                                await this.router.navigateByUrl(absoluteRoutePaths.update);
                            },
                        },
                        {
                            id: 'postponeAction',
                            text: 'Dismiss',
                            title: 'Postpone update',
                            importance: 'Tertiary',
                            execute: (toasterId) => this.notificationService.removeNotification(toasterId),
                        },
                    ];
                    this.notificationService.removeAllNotifications();

                    this.notificationService.toaster.info(
                        {
                            emphasized: 'Update available',
                            message: 'Terminal update available.',
                            emphasisBlock: true,
                        },
                        { actions: updateActions },
                    );
                    return true;
                }),
            ),
        { dispatch: false },
    );

    checkForUpdatesScheduled$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(initializeServiceWorker),
            filter(() => this.swUpdate.isEnabled),
            switchMap(() => interval(60000)),
            map(() => checkForUpdates()),
        );
    });

    checkForUpdates$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(checkForUpdates),
                tap(() => this.swUpdate.checkForUpdate()),
                map(() => true),
            ),
        { dispatch: false },
    );

    constructor(
        private actions$: Actions,
        private swUpdate: SwUpdate,
        private notificationService: PrivaNotificationsService,
        private store: Store,
        private router: Router,
    ) {}
}
